import { useMemo } from 'react';
import { useParams } from 'react-router';
import { EditOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Tooltip, Typography } from 'antd';

import DisplayValue from 'components/DisplayValue/DisplayValue';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import useModalControls from 'hooks/useModalControls';

import { DECEASED_STATUS } from 'utilities/constants';
import EditDeceasedStatusModal from './EditDeceasedStatusModal';
import useAccountQuery from 'hooks/useAccountQuery';
import useAgent from 'hooks/useAgent';

const { Title } = Typography;

const LOADING_SKELETON = {
  title: false,
  paragraph: { rows: 2, width: ['120px', '140px'] },
};

const DeceasedStatus = () => {
  const agent = useAgent();
  const { customerId } = useParams();
  const { accountQuery, accounts, customerInDebtSale } =
    useAccountQuery(customerId);
  const editDeceasedStatusModal = useModalControls();

  const deceasedStatus = useMemo(() => {
    let deceasedStatus = DECEASED_STATUS.UNAVAILABLE;

    if (accounts?.length) {
      const statusObjects = accounts.map((account) => account.statuses);
      const isDeceased = !!statusObjects.find((statuses) => {
        return statuses.isDeceased;
      });

      deceasedStatus = DECEASED_STATUS.NOT_DECEASED;

      if (isDeceased) {
        deceasedStatus = DECEASED_STATUS.DECEASED;
      } else {
        const hasPendingDeceasedStatus = statusObjects.find((statuses) => {
          return statuses.raw.find(({ statusCode, reasonCode }) => {
            return statusCode === 'WA' && reasonCode === 'W1';
          });
        });
        if (hasPendingDeceasedStatus) {
          deceasedStatus = DECEASED_STATUS.PENDING_DECEASED;
        }
      }
    }
    return deceasedStatus;
  }, [accounts]);

  const confirmedDeceased = deceasedStatus === DECEASED_STATUS.DECEASED;

  return (
    <div>
      <EditDeceasedStatusModal
        {...editDeceasedStatusModal}
        accounts={accounts}
        deceasedStatus={deceasedStatus}
      />
      <Flex justify="space-between">
        <Title level={5}>Deceased</Title>
        <Tooltip
          title={
            confirmedDeceased
              ? 'Cardholder is confirmed deceased. This status cannot be reversed within Mission Control.'
              : customerInDebtSale && !agent.isSuperUser
                ? 'Requires Super User privilege to update customer details for customers with accounts in a debt sale'
                : ''
          }
          placement="topRight"
        >
          <Button
            data-testid="edit-deceased-status-button"
            type="link"
            icon={<EditOutlined style={{ fontSize: '1.5rem' }} />}
            onClick={editDeceasedStatusModal.show}
            disabled={
              confirmedDeceased || (customerInDebtSale && !agent.isSuperUser)
            }
            size="small"
            Button
          />
        </Tooltip>
      </Flex>
      <Divider style={{ margin: '0 0 var(--spacing-sm)' }} />
      <QueryBoundary query={accountQuery} skeleton={LOADING_SKELETON}>
        <DisplayValue label="Current Status:">
          <span data-testid="deceased-status">
            {deceasedStatus?.replace('_', ' ')}
          </span>
        </DisplayValue>
      </QueryBoundary>
    </div>
  );
};

export default DeceasedStatus;
